import React, { Fragment } from 'react';
import {
  $createParagraphNode,
  DOMConversionMap,
  DOMExportOutput,
  NodeKey,
  SerializedElementNode,
  SerializedLexicalNode,
  TextFormatType,
} from "lexical";
import { AlignMenuItem } from "./types";
import { EditorConfig, EditorThemeClasses } from "lexical/LexicalEditor";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { InitialConfigType } from "@lexical/react/LexicalComposer";
import { LexicalEditor } from "lexical";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { AlignCenterOutlined, AlignLeftOutlined, AlignRightOutlined } from "@ant-design/icons";
import { TableNode, TableCellNode, TableRowNode } from "@lexical/table";
import { ImageNode } from "./plugins/ImageNode";

import { $createTextNode, $getRoot, ElementNode } from "lexical";
import { WidgetNode } from "./components/Widget";
import { BeautifulMentionsTheme } from "lexical-beautiful-mentions";
import { BeautifulMentionNode } from "lexical-beautiful-mentions";
import { DecoratorNode, TextNode } from 'lexical';
const beautifulMentionsTheme: BeautifulMentionsTheme = {
  "@": "highlighted",
  "@Focused": "outline-none shadow-md ...",
  "due:": {
    trigger: "text-blue-400 ...",
    value: "text-orange-400 ...",
  },
}
const logoTemplateUrl = process.env.REACT_APP_NOTIFICATION_TEMPLATE_LOGO || "";

export const theme: EditorThemeClasses = {
  blockCursor: "blockCursor",
  characterLimit: "characterLimit",
  code: "code",
  beautifulMentions: beautifulMentionsTheme,
  codeHighlight: {
    atrule: "tokenAttr",
    attr: "tokenAttr",
    boolean: "tokenProperty",
    builtin: "tokenSelector",
    cdata: "tokenComment",
    char: "tokenSelector",
    class: "tokenFunction",
    "class-name": "tokenFunction",
    comment: "tokenComment",
    constant: "tokenProperty",
    deleted: "tokenProperty",
    doctype: "tokenComment",
    entity: "tokenOperator",
    function: "tokenFunction",
    important: "tokenVariable",
    inserted: "tokenSelector",
    keyword: "tokenAttr",
    namespace: "tokenVariable",
    number: "tokenProperty",
    operator: "tokenOperator",
    prolog: "tokenComment",
    property: "tokenProperty",
    punctuation: "tokenPunctuation",
    regex: "tokenVariable",
    selector: "tokenSelector",
    string: "tokenSelector",
    symbol: "tokenProperty",
    tag: "tokenProperty",
    url: "tokenOperator",
    variable: "tokenVariable",
  },
  embedBlock: {
    base: "embedBlock",
    focus: "embedBlockFocus",
  },
  hashtag: "hashtag",
  heading: {
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
    h6: "h6",
  },
  image: "editor-image",
  indent: "indent",
  link: "link",
  list: {
    listitem: "listItem",
    listitemChecked: "listItemChecked",
    listitemUnchecked: "listItemUnchecked",
    nested: {
      listitem: "nestedListItem",
    },
    olDepth: ["ol1", "ol2", "ol3", "ol4", "ol5"],
    ul: "ul",
  },
  ltr: "ltr",
  mark: "mark",
  markOverlap: "markOverlap",
  paragraph: "paragraph",
  quote: "quote",
  rtl: "rtl",
  table: "table",
  tableAddColumns: "tableAddColumns",
  tableAddRows: "tableAddRows",
  tableCell: "tableCell",
  tableCellActionButton: "tableCellActionButton",
  tableCellActionButtonContainer: "tableCellActionButtonContainer",
  tableCellEditing: "tableCellEditing",
  tableCellHeader: "tableCellHeader",
  tableCellPrimarySelected: "tableCellPrimarySelected",
  tableCellResizer: "tableCellResizer",
  tableCellSelected: "tableCellSelected",
  tableCellSortedIndicator: "tableCellSortedIndicator",
  tableResizeRuler: "tableCellResizeRuler",
  tableSelected: "tableSelected",
  text: {
    bold: "textBold",
    code: "textCode",
    italic: "textItalic",
    strikethrough: "textStrikethrough",
    subscript: "textSubscript",
    superscript: "textSuperscript",
    underline: "textUnderline",
    underlineStrikethrough: "textUnderlineStrikethrough",
  },
  templateHeading: {
    color: "red",
  },
};

const icon: React.CSSProperties = {
  color: "black",
  cursor: "pointer",
  fontSize: "16px",
};
/**
 * Catch any errors that occur during Lexical updates and log them
 * or throw them as needed. If you don't throw them, Lexical will
 * try to recover gracefully without losing user data.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const onError = (error: Error, editor: LexicalEditor): void => {
};

export class CustomTemplate extends ElementNode {
  static getType() {
    return "custom-template";
  }

  static clone(node: CustomTemplate) {
    return new CustomTemplate(node.__key);
  }

  createDOM() {
    const TemplateContainer = document.createElement("div");

    //container1
    const container1 = document.createElement("div");
    container1.style.width = "100%";
    container1.style.height = "40px";
    container1.style.backgroundColor = "#1677FF";

    const heading = document.createElement("h5");
    heading.textContent = "Document Forwarded";
    heading.style.color = "white";
    heading.style.margin = "0px";
    heading.style.padding = "9px 24px";
    heading.style.fontFamily = "inherit";
    heading.style.fontSize = "14px";
    heading.style.fontWeight = "600";
    container1.appendChild(heading);

    //container2
    const container2 = document.createElement("div");
    container2.style.width = "620px";
    container2.style.height = "184px";
    container2.style.backgroundColor = "#BAE0FF";
    container2.style.padding = "24px";

    const line1 = document.createElement("p");
    line1.textContent = "Hello RECIPIENT ,";
    line1.style.padding = "0px";
    line1.style.margin = "0px";
    line1.style.marginBottom = "16px";
    line1.style.fontFamily = "inherit";
    line1.style.fontSize = "16px";
    line1.style.fontWeight = "400";

    const line2 = document.createElement("p");
    line2.textContent =
      'CONNECTED USER  forwarded you the document "Document Name" ';
    line2.style.padding = "0px";
    line2.style.margin = "0px";
    line2.style.marginBottom = "16px";
    line2.style.fontFamily = "inherit";
    line2.style.fontSize = "16px";
    line2.style.fontWeight = "400";

    const button = document.createElement("btn");
    button.textContent = "Open the Document";
    button.style.height = "40px";
    button.style.width = "572px";
    button.style.backgroundColor = "#FFFFFF";
    button.style.display = "flex";
    button.style.borderRadius = "8px";
    button.style.justifyContent = "center";
    button.style.alignItems = "center";
    button.style.fontSize = "16px";
    button.style.fontWeight = "400";
    button.style.lineHeight = "24px";
    button.style.border = "dashed 1px #D9D9D9";

    container2.append(line1);
    container2.append(line2);
    container2.append(button);
    TemplateContainer.appendChild(container1);
    TemplateContainer.appendChild(container2);
    return TemplateContainer;
  }

  updateDOM(prevNode: CustomTemplate, dom: HTMLElement): boolean {
    return false;
  }

  exportJSON(): SerializedElementNode<SerializedLexicalNode> {
    return { ...super.exportJSON() };
  }
}

export function $createCustomTemplate(): CustomTemplate {
  return new CustomTemplate();
}

// logo image
class LogoImage extends ElementNode {
  static getType() {
    return "logo-image";
  }

  static clone(node: LogoImage) {
    return new LogoImage();
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("img");
    element.src = logoTemplateUrl;
    element.dataset.name = this.getType();
    element.style.width = "161px";
    element.style.height = "64px";
    element.style.display = "block";
    element.style.left = "-20px";
    element.style.position = "relative";

    return { element };
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const dom = document.createElement("img");
    dom.dataset.name = this.getType();
    dom.src = logoTemplateUrl;
    dom.style.width = "161px";
    dom.style.height = "64px";
    dom.style.display = "block";
    dom.style.left = "-20px";
    dom.style.position = "relative";

    return dom;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (domNode: HTMLElement) => {
        if (domNode.getAttribute("data-name") !== "logo-image") {
          return null;
        }

        return {
          conversion: (domNode: HTMLElement) => {
            return {
              node: $createCustomLogo(),
            };
          },
          priority: 1,
        };
      },
    };
  }

  updateDOM(prevNode: LogoImage, dom: HTMLElement): boolean {
    return false;
  }

  exportJSON(): SerializedElementNode<SerializedLexicalNode> {
    return { ...super.exportJSON() };
  }
}

export function $createCustomLogo(): LogoImage {
  return new LogoImage();
}
export function $createCustomFooter(): FooterTag {
  return new FooterTag();
}
export function $createCustomMentions(label:any, id: any): CustomMentions {
  return new CustomMentions(label, id);
}



// heading
class HeadingTag extends ElementNode {
  __margin = "0px";
  __padding = "10px";
  __fontFamily = "inherit";
  __fontSize = "16px";
  __fontWeight = "400";
  __backgroundColor = "#1677FF";
  __color = "#ffffff";

  static getType() {
    return "heading-tag";
  }

  static clone(node: HeadingTag) {
    return new HeadingTag();
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("p");
    element.style.margin = this.__margin;
    element.style.padding = this.__padding;
    element.style.fontFamily = this.__fontFamily;
    element.style.fontSize = this.__fontSize;
    element.style.fontWeight = this.__fontWeight;
    element.style.color = this.__color;
    element.style.backgroundColor = this.__backgroundColor;
    element.dataset.name = this.getType();

    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      p: (domNode: HTMLElement) => {
        if (domNode.getAttribute("data-name") !== "heading-tag") {
          return null;
        }

        return {
          conversion: (domNode: HTMLElement) => {
            return {
              node: $createCustomHeading(),
            };
          },
          priority: 1,
        };
      },
    };
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const dom = document.createElement("p");
    dom.style.margin = this.__margin;
    dom.style.padding = this.__padding;
    dom.style.padding = this.__padding;
    dom.style.fontFamily = this.__fontFamily;
    dom.style.fontSize = this.__fontSize;
    dom.style.fontWeight = this.__fontWeight;
    dom.style.backgroundColor = this.__backgroundColor;
    dom.style.color = this.__color;

    return dom;
  }

  updateDOM(prevNode: HeadingTag, dom: HTMLElement): boolean {
    return false;
  }

  exportJSON(): SerializedElementNode<SerializedLexicalNode> {
    return { ...super.exportJSON() };
  }
}
export class StyledTextNode extends TextNode {
  __fontSize: string;
  __text: string;

  constructor(fontSize: string, text: string,) {
    super(text);
    this.__fontSize = fontSize;
    this.__text = text;
  }

  static getType(): string {
    return "styled-node";
  }

  static clone(node: StyledTextNode): StyledTextNode {
    return new StyledTextNode(node.__fontSize, node.__text,);
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("span");
    element.style.fontSize = this.__fontSize;
    element.innerText = this.__text;
    element.setAttribute("data-name","styled-node")
    return { element };
  }
  
  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (domNode.getAttribute("data-name") !== "styled-node") {
          return null;
        }
        const fontSize = domNode.style.fontSize;
        const text = domNode.textContent || "";
        return {
          conversion: () => ({
            node: new StyledTextNode( fontSize, text),
          }),
          priority: 1,
        };
      },
    };
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.style.fontSize = this.__fontSize;
    span.textContent = this.getTextContent();
    span.setAttribute("data-name", "styled-node");
    return span;
  }

  updateDOM(prevNode: StyledTextNode): boolean {
    return this.__fontSize !== prevNode.__fontSize && this.__text !== prevNode.__text;;
  }
}
// Footer
interface SerializedCustomMentionsNode extends SerializedElementNode {
  label: string;
}
class CustomMentions extends ElementNode {
  __label: string;
  __id: string;

  // Constructor to accept a label
  constructor(label: string, id: string, key?: NodeKey) {
    super(key);
    this.__label = label || "Recipient"; // Default to "Recipient" if no label is passed
    this.__id = id;
  }

  // Node type
  static getType() {
    return "custom-mention-tag";
  }

  // Clone method with dynamic label
  static clone(node: CustomMentions) {
    return new CustomMentions(node.__label, node.__id, node.__key);
  }

  // DOM export logic using dynamic label
  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("span");
    element.classList.add("highlighted");
    element.setAttribute("data-beautiful-mention", `@${this.__label}`);
    element.setAttribute("data-lexical-beautiful-mention", `true`);
    element.setAttribute("data-lexical-beautiful-mention-trigger", `@`);
    element.setAttribute("data-lexical-beautiful-mention-value", `${this.__label}`);
    element.setAttribute("data-name", "custom-mention-tag");
    element.setAttribute("contenteditable", "false");
    element.setAttribute("data-lexical-decorator", "true");
    return { element };
  }

  // DOM import logic remains unchanged
  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (domNode.getAttribute("data-name") !== "custom-mention-tag") {
          return null;
        }
        return {
          conversion: (domNode: HTMLElement) => {
            const label = domNode.getAttribute("data-beautiful-mention") || "Recipient";
            const id = domNode.getAttribute("data-lexical-beautiful-mention-value") || "";
            return {
              node: new CustomMentions(label, id),
            };
          },
          priority: 1,
        };
      },
    };
  }

  // DOM creation using dynamic label
  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const dom = document.createElement("span");
    dom.classList.add("highlighted");
    dom.setAttribute("data-beautiful-mention", `@${this.__label}`);
    dom.setAttribute("data-lexical-beautiful-mention", `true`);
    dom.setAttribute("data-lexical-beautiful-mention-trigger", `@`);
    dom.setAttribute("data-lexical-beautiful-mention-value", `${this.__label}`);
    dom.setAttribute("data-name", "custom-mention-tag");
    dom.setAttribute("contenteditable", "false");
    dom.setAttribute("data-lexical-decorator", "true");
    return dom;
  }

  updateDOM(prevNode: CustomMentions, dom: HTMLElement): boolean {
    if (this.__label !== prevNode.__label || this.__id !== prevNode.__id) {
      dom.setAttribute("data-beautiful-mention", `@${this.__label}`);
      dom.setAttribute("data-lexical-beautiful-mention-value", `${this.__label}`);
    }
    return true;
  }

  exportJSON(): SerializedCustomMentionsNode {
    return {
      ...super.exportJSON(),
      label: this.__label,
    };
  }
}
//Custom mention
class FooterTag extends ElementNode {
  __margin = "33px 0 0 0";
  __padding = "10px";
  __fontFamily = "inherit";
  __fontSize = "16px";
  __fontWeight = "bold";
  __backgroundColor = "#F3F3F3";
  __color = "#141BCB";
  __borderColor = "#141BCB";
  __borderWidth =  "1px 2px 2px 15px";
  __borderStyle = "solid";
  __borderLeftColor = "#141BCB"
  __display = "block"


  static getType() {
    return "footer-tag";
  }

  static clone(node: FooterTag) {
    return new FooterTag();
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("a");
    element.style.margin = this.__margin;
    element.style.padding = this.__padding;
    element.style.fontFamily = this.__fontFamily;
    element.style.fontSize = this.__fontSize;
    element.style.fontWeight = this.__fontWeight;
    element.style.color = this.__color;
    element.style.backgroundColor = this.__backgroundColor;
    element.style.borderColor = this.__borderColor;
    element.style.borderWidth = this.__borderWidth;
    element.style.borderStyle = this.__borderStyle;
    element.style.borderLeftColor = this.__borderLeftColor;
    element.style.display = this.__display;
    element.dataset.name = this.getType();
    element.style.textAlign = "center"
    element.style.cursor = "pointer"
    element.setAttribute("action-link","OPEN_THE_DOCUMENT")
    element.setAttribute("data-name","footer-tag")

    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: (domNode: HTMLElement) => {
        if (domNode.getAttribute("data-name") !== "footer-tag") {
          return null;
        }

        return {
          conversion: (domNode: HTMLElement) => {
            return {
              node: $createCustomFooter(),
            };
          },
          priority: 1,
        };
      },
    };
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const dom = document.createElement("a");
    dom.style.margin = this.__margin;
    dom.style.padding = this.__padding;
    dom.style.padding = this.__padding;
    dom.style.fontFamily = this.__fontFamily;
    dom.style.fontSize = this.__fontSize;
    dom.style.fontWeight = this.__fontWeight;
    dom.style.backgroundColor = this.__backgroundColor;
    dom.style.color = this.__color;
    dom.style.borderColor = this.__borderColor;
    dom.style.borderWidth = this.__borderWidth;
    dom.style.borderStyle = this.__borderStyle;
    dom.style.borderLeftColor = this.__borderLeftColor;
    dom.style.display = this.__display;
    dom.style.textAlign = "center";
    dom.style.cursor = "pointer"
    dom.setAttribute("action-link","OPEN_THE_DOCUMENT")
    dom.setAttribute("data-name","footer-tag")
    return dom;
  }

  updateDOM(prevNode: FooterTag, dom: HTMLElement): boolean {
    return true;
  }

  exportJSON(): SerializedElementNode<SerializedLexicalNode> {
    return { ...super.exportJSON() };
  }
}
//Button Link
class LinkButtonTag extends ElementNode {
  __margin = "0px auto";
  __padding = "8px 18px";
  __fontFamily = "inherit";
  __fontSize = "16px";
  __fontWeight = "400";
  __backgroundColor = "rgb(22, 119, 255)";
  __color = "white";
  __width = "fit-content";
  __cursor = "pointer"
  __textAlign = "center"
  __borderRadius = "8px";
  __display = "block";


  static getType() {
    return "link-button-tag";
  }

  static clone(node: LinkButtonTag) {
    return new LinkButtonTag();
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("span");
    element.classList.add("link-button")
    element.classList.add("link-button-tag")
    element.style.margin = this.__margin;
    element.style.padding = this.__padding;
    element.style.fontFamily = this.__fontFamily;
    element.style.fontSize = this.__fontSize;
    element.style.fontWeight = this.__fontWeight;
    element.style.color = this.__color;
    element.style.backgroundColor = this.__backgroundColor;
    element.style.width = this.__width;
    element.style.borderRadius = this.__borderRadius;
    element.dataset.name = this.getType();
    element.style.cursor = this.__cursor;
    element.style.display = this.__display;
    element.style.textAlign = this.__textAlign;
    element.setAttribute("data-name","link-button-tag")

    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (domNode.getAttribute("data-name") !== "link-button-tag") {
          return null;
        }

        return {
          conversion: (domNode: HTMLElement) => {
            return {
              node: $createCustomLinkButton(),
            };
          },
          priority: 1,
        };
      },
    };
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const dom = document.createElement("span");
    dom.classList.add("link-button")
    dom.classList.add("link-button-tag")
    dom.style.margin = this.__margin;
    dom.style.padding = this.__padding;
    dom.style.padding = this.__padding;
    dom.style.fontFamily = this.__fontFamily;
    dom.style.fontSize = this.__fontSize;
    dom.style.fontWeight = this.__fontWeight;
    dom.style.width = this.__width;
    dom.style.backgroundColor = this.__backgroundColor;
    dom.style.color = this.__color;
    dom.style.borderRadius = this.__borderRadius;
    dom.style.cursor = this.__cursor;
    dom.style.textAlign = this.__textAlign;
    dom.style.display = this.__display;
    dom.setAttribute("data-name","link-button-tag")

    return dom;
  }

  updateDOM(prevNode: LinkButtonTag, dom: HTMLElement): boolean {
    return false;
  }

  exportJSON(): SerializedElementNode<SerializedLexicalNode> {
    return { ...super.exportJSON() };
  }
}

export function $createCustomHeading(): HeadingTag {
  return new HeadingTag();
}
export function $createCustomLinkButton(): LinkButtonTag {
  return new LinkButtonTag();
}

// body
class TitleTag extends ElementNode {
  __margin = "0 0 15px 0";
  __padding = "0px";
  __fontFamily = "inherit";
  __fontSize = "20px";
  __fontWeight = "bold";
  __color = "#000000";
  __minHeight = "184px";
 // __maxWidth = "700px";
  __height = "auto";
  __backgroundColor = "white";

  static getType() {
    return "title-tag";
  }

  static clone(node: TitleTag) {
    return new TitleTag();
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("p");
    element.style.margin = this.__margin;
    element.style.padding = this.__padding;
    element.style.fontFamily = this.__fontFamily;
    element.style.fontSize = this.__fontSize;
    element.style.fontWeight = this.__fontWeight;
    element.style.color = this.__color;
   // element.style.maxWidth = this.__maxWidth;
    element.style.backgroundColor = this.__backgroundColor;
    element.dataset.name = this.getType();
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      p: (domNode: HTMLElement) => {
        if (domNode.getAttribute("data-name") !== "title-tag") {
          return null;
        }

        return {
          conversion: (domNode: HTMLElement) => {
            return {
              node: $createCustomTitle(),
            };
          },
          priority: 1,
        };
      },
    };
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const dom = document.createElement("p");
    dom.style.margin = this.__margin;
    dom.style.padding = this.__padding;
    dom.style.fontFamily = this.__fontFamily;
    dom.style.fontSize = this.__fontSize;
    dom.style.fontWeight = this.__fontWeight;
    dom.style.color = this.__color;
    dom.style.backgroundColor = this.__backgroundColor;
    //dom.style.maxWidth = this.__maxWidth;

    return dom;
  }

  updateDOM(prevNode: TitleTag, dom: HTMLElement): boolean {
    return false;
  }

  exportJSON(): SerializedElementNode<SerializedLexicalNode> {
    return { ...super.exportJSON() };
  }
}
class BodyTag extends ElementNode {
  __margin = "auto";
  __padding = "0px";
  __fontFamily = "inherit";
  __fontSize = "14px";
  __fontWeight = "400";
  __color = "#000000";
  __minHeight = "184px";
  //__maxWidth = "700px";
  __height = "auto";
  __backgroundColor = "white";

  static getType() {
    return "body-tag";
  }

  static clone(node: BodyTag) {
    return new BodyTag();
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("p");
    element.style.margin = this.__margin;
    element.style.padding = this.__padding;
    element.style.fontFamily = this.__fontFamily;
    element.style.fontSize = this.__fontSize;
    element.style.fontWeight = this.__fontWeight;
    element.style.color = this.__color;
    //element.style.maxWidth = this.__maxWidth;
    element.style.backgroundColor = this.__backgroundColor;
    element.dataset.name = this.getType();
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      p: (domNode: HTMLElement) => {
        if (domNode.getAttribute("data-name") !== "body-tag") {
          return null;
        }

        return {
          conversion: (domNode: HTMLElement) => {
            return {
              node: $createCustomBody(),
            };
          },
          priority: 1,
        };
      },
    };
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const dom = document.createElement("p");
    dom.style.margin = this.__margin;
    dom.style.padding = this.__padding;
    dom.style.fontFamily = this.__fontFamily;
    dom.style.fontSize = this.__fontSize;
    dom.style.fontWeight = this.__fontWeight;
    dom.style.color = this.__color;
    dom.style.backgroundColor = this.__backgroundColor;
    //dom.style.maxWidth = this.__maxWidth;

    return dom;
  }

  updateDOM(prevNode: HeadingTag, dom: HTMLElement): boolean {
    return false;
  }

  exportJSON(): SerializedElementNode<SerializedLexicalNode> {
    return { ...super.exportJSON() };
  }
}

export function $createCustomBody(): BodyTag {
  return new BodyTag();
}
export function $createCustomTitle(): TitleTag {
  return new TitleTag();
}


// body
class CustomBotton extends ElementNode {
  __margin = "0px";
  __padding = "10px";
  __fontFamily = "inherit";
  __fontSize = "16px";
  __fontWeight = "400";
  __color = "#000000";
  __width = "200px";
  // __minHeight = "184px";
  __height = "auto";
  __backgroundColor = "#BAE0FF";

  static getType() {
    return "button-tag";
  }

  static clone(node: CustomBotton) {
    return new CustomBotton();
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("p");
    element.style.margin = this.__margin;
    element.style.padding = this.__padding;
    element.style.fontFamily = this.__fontFamily;
    element.style.fontSize = this.__fontSize;
    element.style.fontWeight = this.__fontWeight;
    element.style.color = this.__color;
    element.style.backgroundColor = this.__backgroundColor;
    element.style.width = this.__width;
    element.dataset.name = this.getType();
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      p: (domNode: HTMLElement) => {
        if (domNode.getAttribute("data-name") !== "button-tag") {
          return null;
        }

        return {
          conversion: (domNode: HTMLElement) => {
            return {
              node: $createCustomBody(),
            };
          },
          priority: 1,
        };
      },
    };
  }

  createDOM(_config: EditorConfig, _editor: LexicalEditor): HTMLElement {
    const dom = document.createElement("p");
    dom.style.margin = this.__margin;
    dom.style.padding = this.__padding;
    dom.style.fontFamily = this.__fontFamily;
    dom.style.fontSize = this.__fontSize;
    dom.style.fontWeight = this.__fontWeight;
    dom.style.color = this.__color;
    dom.style.width = this.__width;
    dom.style.backgroundColor = this.__backgroundColor;

    return dom;
  }

  updateDOM(prevNode: HeadingTag, dom: HTMLElement): boolean {
    return false;
  }

  exportJSON(): SerializedElementNode<SerializedLexicalNode> {
    return { ...super.exportJSON() };
  }
}
export function $createCustomButton(): CustomBotton {
  return new CustomBotton();
}

export class CustomSpanNode extends DecoratorNode<JSX.Element> {
  __label: string;
  __className: string;
  __attributes: Record<string, string>;

  static getType(): string {
    return "custom-span";
  }

  static clone(node: CustomSpanNode): CustomSpanNode {
    return new CustomSpanNode(node.__label, node.__className, node.__attributes, node.__key);
  }

  constructor(label: string, className: string, attributes: Record<string, string>, key?: NodeKey) {
    super(key);
    this.__label = label
    this.__className = className;
    this.__attributes = attributes;
  }

  // createDOM(_config: EditorConfig): HTMLElement {
  //   const span = document.createElement("span");
  //   span.classList.add(this.__className);
  //   span.innerText = `[${this.__label}]`;

  //   Object.entries(this.__attributes).forEach(([key, value]) => {
  //     span.setAttribute(key, value);
  //   });

  //   return span;
  // }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.setAttribute('data-lexical-decorator', 'true');
    span.setAttribute('contenteditable', 'false');

    const innerSpan = document.createElement('span');
    innerSpan.classList.add('highlighted');
    Object.entries(this.__attributes).forEach(([key, value]) => {
      span.setAttribute(key, value);
    });
    // innerSpan.setAttribute('data-beautiful-mention', `@${this.__id}`);
    innerSpan.innerText =  `@${this.__label}`

    span.appendChild(innerSpan);
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  // exportDOM(): DOMExportOutput {
  //   const element = document.createElement("span");
  //   element.classList.add(this.__className);
  //   element.innerText = `[${this.__label}]`;

  //   Object.entries(this.__attributes).forEach(([key, value]) => {
  //     element.setAttribute(key, value);
  //   });
  //   return { element };
  // }

  static importDOM(): any {
    return null;
  }

  isInline(): boolean {
    return true;
  }

  static importJSON(): any {
    return null;
  }

  exportJSON(): any {
    return {
      type: "custom-span",
      version: 1,
      className: this.__className,
      attributes: this.__attributes,
      label: this.__label
    };
  }

  decorate(): JSX.Element {
    return (
      <span data-lexical-decorator="true" contentEditable={false} style={{ display: 'inline' }}>
        <span className="highlighted">@{this.__label}</span>
      </span>
    );
  }
}

// export function $createCustomSpanNode(label: string, className: string, attributes: Record<string, string>): CustomSpanNode {
//   return new CustomSpanNode(label, className, attributes);
// }


function template() {
  const root = $getRoot();

  if (root.getFirstChild() === null) {
    const heading = $createCustomHeading().append(
      $createTextNode("EMAIL FORWARDED")
    );
    let params = new URLSearchParams(window.location.href)
    let notificationtype:any = params.get("notificationType")
    let currentLang = params.get("language") || "english";
    const notificationMapping:any = {
      "EMAIL_SENT_TO_COLLABORATORS": {"english": "View the case in flxflow", "japanese": "flxflowにアクセスして内容を確認する"},
      "SUMMARY_FOR_EVERY_EVENT_OF_THE_DAY": {"english": "View the case in flxflow", "japanese": "flxflowにアクセスしてスペースを確認する"},
      "CHATROOM_NOTIFICATION": {"english": "View the message in flxflow", "japanese": "flxflowにアクセスしてメッセージを確認する"},
      "CHATROOM_EXPORT_NOTIFICATION": {"english": "View the export file", "japanese": "出力ファイルにアクセスする"},
      "WORKFLOW_STEP_NOTIFICATION":{"english": "View the case in flxflow", "japanese": "flxflowにアクセスして内容を確認する"},
      "WORKFLOW_STEP_REFUSAL":{"english": "View the case in flxflow", "japanese": "flxflowにアクセスして内容を確認する"},
      "WORKFLOW_STEP_WAITING":{"english": "View the case in flxflow", "japanese": "flxflowにアクセスして内容を確認する"},
      "WORKFLOW_STEP_WAITING_WITH_DUE_DATE":{"english": "View the case in flxflow", "japanese": "flxflowにアクセスして内容を確認する"},
      "WORKFLOW_END_NOTIFICATION":{"english": "View the case in flxflow", "japanese": "flxflowにアクセスして内容を確認する"},
      "WORKFLOW_STEP_SENT_BACK": {"english": "View the case in flxflow", "japanese": "flxflowにアクセスして内容を確認する"},
      "STEP_DUE_DATE_NOTIFICATION":{"english": "View the case in flxflow", "japanese": "flxflowにアクセスして内容を確認する"},
    }
    if(currentLang === "english") {
      if(notificationtype == "SUMMARY_FOR_EVERY_EVENT_OF_THE_DAY"){
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
        const spaceName = $createCustomMentions("Space name", "Space name").append($createTextNode("@Space name"));
        const eventList = $createCustomMentions("Event List", "Event List").append($createTextNode("@Event List"));
        const line1 = $createCustomBody().append($createTextNode("You've got reminders today on the space "));
        line1.append(spaceName)
        line1.append($createTextNode("  "))
        const line2 = $createCustomBody().append($createTextNode("Here is the list of your reminders: "));
        line2.append(eventList)
        line2.append($createTextNode("  "))
  
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping[notificationtype][currentLang] : "OPEN THE DOCUMENT"));
        title.append(recipient)
        title.append($createTextNode("  "))
        body.append(title)
        body.append(line1)
        body.append(line2)
        body.append(button)
        root.append(body);
      } else if(notificationtype == "CHATROOM_NOTIFICATION"){
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
        const chatroomMessageSender = $createCustomMentions("Chatroom message sender", "Chatroom message sender").append($createTextNode("@Chatroom message sender"));
        const chatroomName = $createCustomMentions("Chatroom name", "Chatroom name").append($createTextNode("@Chatroom name"));
        const documentId = $createCustomMentions("Request Id", "Request Id").append($createTextNode("@Request Id"));
        const chatroomMessage = $createCustomMentions("Chatroom message", "Chatroom message").append($createTextNode("@Chatroom message"));
  
        const line1 = $createCustomBody()
        line1.append(chatroomMessageSender)
        line1.append($createTextNode(" sent a new message in the chatroom "))
        line1.append(chatroomName)
        line1.append($createTextNode(" in the case "))
        line1.append(documentId)
        line1.append($createTextNode("  "))
  
        const line2 = $createCustomBody()
        line2.append($createTextNode("Here is the message he sent:"));
        const line3 = $createCustomBody();
        line3.append(chatroomMessage);
  
  
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping[notificationtype][currentLang] : "OPEN THE DOCUMENT"));
        title.append(recipient)
        title.append($createTextNode("  "))
        body.append(title)
        body.append(line1)
        body.append($createTextNode("  "))
        body.append(line2);
        body.append(line3)
        body.append($createTextNode("  "))
        body.append(button)
        root.append(body);
      } else if( notificationtype == "CHATROOM_EXPORT_NOTIFICATION"){
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
        const spaceName = $createCustomMentions("Space name", "Space name").append($createTextNode("@Space name"));
        const exportedFileName = $createCustomMentions("Exported file name", "Exported file name").append($createTextNode("@Exported file name"));
        const line1 = $createCustomBody()
        line1.append($createTextNode("The export "))
        line1.append(exportedFileName)
        line1.append($createTextNode(" that you have asked for the space "))
        line1.append(spaceName)
        line1.append($createTextNode(" is now available on Google Drive. "))
        line1.append($createTextNode("  "))
      
        const line3 = $createCustomBody().append($createTextNode("Please click the button below to access the application."))
        line3.append($createTextNode("  "))
  
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping[notificationtype][currentLang] : "OPEN THE DOCUMENT"));
        title.append(recipient)
        title.append($createTextNode("  "))
        body.append(title)
        body.append(line1)
        body.append($createTextNode("  "))
        body.append(line3)
        body.append(button)
        root.append(body);
      } else if(notificationtype == "STEP_DUE_DATE_NOTIFICATION"){ 
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
  
        const documentId = $createCustomMentions("Request Id", "Request Id").append($createTextNode("@Request Id"));
        const workflowStep = $createCustomMentions("Workflow step", "Workflow step").append($createTextNode("@Workflow step"));
        const dueDate = $createCustomMentions("Due date", "Due date").append($createTextNode("@Due date"));
  
        const line1 = $createCustomBody()
        line1.append($createTextNode("The application "))
        line1.append(documentId)
        line1.append($createTextNode(" is waiting for your approval at the workflow step "))
        line1.append(workflowStep)
        line1.append($createTextNode("  "))
  
        const line2 = $createCustomBody()
        line2.append($createTextNode("The approval for this step is required before: "))
        line2.append(dueDate)
        line2.append($createTextNode("  "))
  
        const line3 = $createCustomBody().append($createTextNode("Please click the button below to access the application."))
        line3.append($createTextNode("  "))
  
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping[notificationtype][currentLang] : "OPEN THE DOCUMENT"));
        title.append(recipient)
        title.append($createTextNode("  "))
        body.append(title)
        body.append(line1)
        body.append($createTextNode("  "))
        body.append(line2)
        body.append(line3)
        body.append(button)
        root.append(body);
      } else if(notificationtype == "WORKFLOW_STEP_NOTIFICATION"){
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
  
        const documentId = $createCustomMentions("Request Id", "Request Id").append($createTextNode("@Request Id"));
        const workflowStep = $createCustomMentions("Workflow step", "Workflow step").append($createTextNode("@Workflow step"));
  
        const line1 = $createCustomBody()
        line1.append($createTextNode("The application "))
        line1.append(documentId)
        line1.append($createTextNode(" is currently at the workflow step "))
        line1.append(workflowStep)
        line1.append($createTextNode("  "))
  
        const line2 = $createCustomBody()
        line2.append($createTextNode("Please use the chatroom feature if you would like to comment on the application."))
        line2.append($createTextNode("  "))
  
        const line3 = $createCustomBody()
        line3.append($createTextNode("------------------------------------ DETAILS OF REQUEST -----------------------------------"))
  
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping[notificationtype][currentLang] : "OPEN THE DOCUMENT"));
        title.append(recipient)
        title.append($createTextNode("  "))
        body.append(title)
        body.append(line1)
        body.append($createTextNode("  "))
        body.append(line2)
        body.append($createTextNode("  "))
        body.append(line3);
        body.append(button)
        root.append(body);
      } else if( notificationtype == "WORKFLOW_STEP_REFUSAL"){
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
  
        const documentId = $createCustomMentions("Request Id", "Request Id").append($createTextNode("@Request Id"));
        const workflowStep = $createCustomMentions("Workflow step", "Workflow step").append($createTextNode("@Workflow step"));
        const lastUpdater = $createCustomMentions("Last updater", "Last updater").append($createTextNode("@Last updater"));
        const lastComment = $createCustomMentions("Last comment", "Last comment").append($createTextNode("@Last comment"));
  
        const line1 = $createCustomBody()
        line1.append($createTextNode("The application "))
        line1.append(documentId)
        line1.append($createTextNode(" has been rejected by "))
        line1.append(lastUpdater)
        line1.append($createTextNode(" at the workflow step "))
        line1.append(workflowStep)
        line1.append($createTextNode("  "))
  
        const line2 = $createCustomBody()
        line2.append($createTextNode("Comment:"))
        line2.append($createTextNode("  "))
  
        const line3 = $createCustomBody()
        line3.append(lastComment)
        line3.append($createTextNode("  "))
  
        const line4 = $createCustomBody()
        line4.append($createTextNode("------------------------------------ DETAILS OF REQUEST -----------------------------------"))
  
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping[notificationtype][currentLang] : "OPEN THE DOCUMENT"));
        title.append(recipient)
        title.append($createTextNode("  "))
        body.append(title)
        body.append(line1)
        body.append($createTextNode("  "))
        body.append(line2)
        body.append(line3)
        body.append($createTextNode(" "))
        body.append(line4)
        body.append(button)
        root.append(body);

        // root.append(logo).append(body);
      } else if(notificationtype == "WORKFLOW_STEP_WAITING"){
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
  
        const documentId = $createCustomMentions("Request Id", "Request Id").append($createTextNode("@Request Id"));
        const workflowStep = $createCustomMentions("Workflow step", "Workflow step").append($createTextNode("@Workflow step"));
  
        const line1 = $createCustomBody()
        line1.append($createTextNode("The application "))
        line1.append(documentId)
        line1.append($createTextNode(" is waiting for your approval at the workflow step "))
        line1.append(workflowStep)
        line1.append($createTextNode("  "))
  
        const line2 = $createCustomBody()
        line2.append($createTextNode("Please click the button below to access the application."))
        line2.append($createTextNode("  "))
  
        const line3 = $createCustomBody()
        line3.append($createTextNode("------------------------------------ DETAILS OF REQUEST -----------------------------------"))
  
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping[notificationtype][currentLang] : "OPEN THE DOCUMENT"));
        title.append(recipient)
        title.append($createTextNode("  "))
        body.append(title)
        body.append(line1)
        body.append($createTextNode("  "))
        body.append(line2)
        body.append($createTextNode(" "))
        body.append(line3)
        body.append(button)
        root.append(body);
        // append(logo).
      } else if(notificationtype == "WORKFLOW_STEP_WAITING_WITH_DUE_DATE") {
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
  
        const documentId = $createCustomMentions("Request Id", "Request Id").append($createTextNode("@Request Id"));
        const workflowStep = $createCustomMentions("Workflow step", "Workflow step").append($createTextNode("@Workflow step"));
        const dueDate = $createCustomMentions("Due date", "Due date").append($createTextNode("@Due date"));
  
        const line1 = $createCustomBody()
        line1.append($createTextNode("The application "))
        line1.append(documentId)
        line1.append($createTextNode(" is waiting for your approval at the workflow step "))
        line1.append(workflowStep)
        line1.append($createTextNode("  "))
  
        const line2 = $createCustomBody()
        line2.append($createTextNode("The approval for this step is required before: "))
        line2.append(dueDate)
        line2.append($createTextNode("  "))
  
        const line3 = $createCustomBody().append($createTextNode("Please click the button below to access the application."))
        line3.append($createTextNode("  "))
  
        const line4 = $createCustomBody()
        line4.append($createTextNode("------------------------------------ DETAILS OF REQUEST -----------------------------------"))
  
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping[notificationtype][currentLang] : "OPEN THE DOCUMENT"));
        title.append(recipient)
        title.append($createTextNode("  "))
        body.append(title)
        body.append(line1)
        body.append($createTextNode("  "))
        body.append(line2)
        body.append(line3)
        body.append($createTextNode(" "))
        body.append(line4)
        body.append(button)
        root.append(body);
      } else if(notificationtype == "WORKFLOW_END_NOTIFICATION") {
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
  
        const documentId = $createCustomMentions("Request Id", "Request Id").append($createTextNode("@Request Id"));
        const publisher = $createCustomMentions("Publisher", "Publisher").append($createTextNode("@Publisher"));
  
        const line1 = $createCustomBody()
        line1.append($createTextNode("The application "))
        line1.append(documentId)
        line1.append($createTextNode(" has been approved by "))
        line1.append(publisher)
        line1.append($createTextNode("  "))
  
        const line2 = $createCustomBody()
        line2.append($createTextNode("------------------------------------ DETAILS OF REQUEST -----------------------------------"))
  
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping[notificationtype][currentLang] : "OPEN THE DOCUMENT"));
        title.append(recipient)
        title.append($createTextNode("  "))
        body.append(title)
        body.append(line1)
        body.append($createTextNode("  "))
        body.append(line2)
        body.append(button)
        root.append(body);
      } else if(notificationtype == "WORKFLOW_STEP_SENT_BACK"){
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
  
        const documentId = $createCustomMentions("Request Id", "Request Id").append($createTextNode("@Request Id"));
        const workflowStep = $createCustomMentions("Workflow step", "Workflow step").append($createTextNode("@Workflow step"));
        const lastUpdater = $createCustomMentions("Last updater", "Last updater").append($createTextNode("@Last updater"));
        const lastComment = $createCustomMentions("Last comment", "Last comment").append($createTextNode("@Last comment"));
  
        const line1 = $createCustomBody()
        line1.append($createTextNode("The workflow step "))
        line1.append(workflowStep)
        line1.append($createTextNode(" has been refused by "))
        line1.append(lastUpdater)
        line1.append($createTextNode(" on the document "))
        line1.append(documentId)
        line1.append($createTextNode("  "))
  
        const line11 = $createCustomBody().append($createTextNode("Please review the application and the following comment. "))
    
        const line2 = $createCustomBody()
        line2.append($createTextNode("Comment:"))
        line2.append($createTextNode("  "))
  
        const line3 = $createCustomBody()
        line3.append(lastComment)
        line3.append($createTextNode("  "))
  
        const line4 = $createCustomBody()
        line4.append($createTextNode("------------------------------------ DETAILS OF REQUEST -----------------------------------"))
  
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping[notificationtype][currentLang] : "OPEN THE DOCUMENT"));
        title.append(recipient)
        title.append($createTextNode("  "))
        body.append(title)
        body.append(line1)
        body.append($createTextNode("  "))
        body.append(line11)
        body.append($createTextNode("  "))
        body.append(line2)
        body.append(line3)
        body.append($createTextNode(" "))
        body.append(line4);
        body.append(button)
        root.append(body);
      } else {
        const body = $createCustomBody()
        const title = $createCustomTitle().append($createTextNode("Hello "))
        const recipient = $createCustomMentions("Recipient", "Recipient").append($createTextNode("@Recipient"));
        const button = $createCustomFooter().append($createTextNode(notificationtype ? notificationMapping?.[notificationtype]?.[currentLang] : "View the case in flxflow"));
        title.append(recipient)
        title.append($createTextNode(","))

        title.append($createTextNode("  "))
        body.append(title)
        body.append($createTextNode("  "))
        body.append(button)
        root.append(body);
      }
    }
    // debugger
    
   
    // const button = $createCustomButton().append($createTextNode("Link"));
    // root.append(logo).append(heading).append(body).append(button);
    //const para = $createParagraphNode().append($createTextNode("write here..."))
    //root.append(para)
  }
}

type SerializedMentionNode = {
  mention: string;
  id: string,
  type: string;
  version: number;
};

export class MentionNode extends DecoratorNode<JSX.Element> {
  __mention: string;
  __id: string;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mention, node.__id);
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const { mention, id } = serializedNode;
    return new MentionNode(mention, id || '');
  }

  constructor(mention: string, id: string ) {
    super();
    this.__mention = mention;
    this.__id = id;
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.setAttribute('data-lexical-decorator', 'true');
    span.setAttribute('contenteditable', 'false');

    const innerSpan = document.createElement('span');
    innerSpan.classList.add('highlighted');
    // Use the mention text for data-beautiful-mention attribute
    innerSpan.setAttribute('data-beautiful-mention', `@${this.__mention}`);
    // Add additional data attributes to ensure compatibility
    innerSpan.setAttribute('data-lexical-beautiful-mention', 'true');
    innerSpan.setAttribute('data-lexical-beautiful-mention-trigger', '@');
    innerSpan.setAttribute('data-lexical-beautiful-mention-value', this.__mention);
    innerSpan.innerText =  `@${this.__mention}`

    span.appendChild(innerSpan);
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  exportJSON(): SerializedMentionNode {
    return {
      mention: this.__mention,
      type: 'mention',
      version: 1,
      id: this.__id
    };
  }

  decorate(): JSX.Element {
    return (
      <span data-lexical-decorator="true" contentEditable={false} style={{ display: 'inline' }}>
        <span className="highlighted">
          @{this.__mention}
        </span>
      </span>
    );
  }
}


export const EDITOR_NODES = [
  CodeNode,
  CodeHighlightNode,
  HeadingNode,
  ImageNode,
  // LinkNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  HorizontalRuleNode,
  TableNode,
  TableCellNode,
  TableRowNode,
  LogoImage,
  HeadingTag,
  BodyTag,
  LinkButtonTag,
  FooterTag,
  CustomMentions,
  TitleTag,
  LinkNode,
  WidgetNode,
  BeautifulMentionNode,
  MentionNode,
  StyledTextNode,
  CustomSpanNode
];

export const initialConfig: InitialConfigType = {
  namespace: "MyEditor",
  theme,
  nodes: EDITOR_NODES,
  onError,
  editable: true,
  editorState: template,
};

export const blockTypeToBlockName = {
  bullet: "Bulleted List",
  check: "Check List",
  code: "Code Block",
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  h4: "Heading 4",
  h5: "Heading 5",
  h6: "Heading 6",
  number: "Numbered List",
  paragraph: "Normal",
  quote: "Quote",
};

export const initialHasFormat: Record<TextFormatType, boolean> = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  subscript: false,
  superscript: false,
  code: false,
  highlight: false,
};

export const alignMenuItems: AlignMenuItem[] = [
  {
    name: "Left Align",
    icon: <AlignLeftOutlined style={icon} />,
    payload: "left",
  },
  {
    name: "Center Align",
    icon: <AlignCenterOutlined style={icon} />,
    payload: "center",
  },
  {
    name: "Right Align",
    icon: <AlignRightOutlined style={icon} />,
    payload: "right",
  },
];
